/* global Word */
import type { GraphLayoutType, TemplateProfile } from '../../templates';
import { runAtCursor } from '../context/runAtCursor';
import { insertExhibit as insertExhibitAtRange } from './ExhibitBuilder';
import { renumberExhibitTables } from './renumbering';

/**
 * Insert an exhibit by layout type using the active template profile.
 *
 * Two-phase approach for Office Online compatibility:
 *   Phase 1 (Word.run #1): Insert the table and write placeholder text.
 *                          This Word.run closes and commits all writes to the server.
 *   Phase 2 (Word.run #2): Renumber all exhibit tables.
 *                          Because the first run is fully committed, cell.body.text
 *                          is now readable for the newly inserted cells — which was
 *                          not possible inside the same Word.run on Office Online.
 */
export async function insertExhibit(layoutType: GraphLayoutType, profile: TemplateProfile): Promise<void> {
	const layout = profile.layouts[layoutType];

	// Phase 1 — insert the table (Word.run opens and closes here)
	await runAtCursor(async (range, context) => {
		await insertExhibitAtRange(profile, layout, context, range, layoutType);
	});

	// Phase 2 — renumber in a fresh Word.run so all written text is visible
	await Word.run(async (context) => {
		await renumberExhibitTables(context, profile);
	});
}

export { insertExhibitAtRange };
export * from './types';
export * from './cellSetup';
export * from './renumbering';
export * from './ExhibitBuilder';
export * from './strategies/FullStrategy';
export * from './strategies/HalfStrategy';
export * from './strategies/DoubleStrategy';
export * from './strategies/DoubleTableStrategy';
export * from './strategies/TableGraphStrategy';
export * from './strategies/HalfWithTextStrategy';
export * from './strategies/FirstPageStrategy';
